import React, {useState, useEffect} from 'react';
import { ArrowLeft, LoadingIcon } from "../../../assets/icons/Icons";
import ProgressBar from "../../ProgressBar";
import "../../../assets/scss/components/multi-step-form.scss"
import "../../../assets/scss/components/cardholder/bulk-create-cards.scss"
import AddPayment from "./AddPayment";
import Finalize from "./Finalize";
import OrderCreationMessage from "./OrderCreationMessage";
import {useTranslation} from "react-i18next";
import Popup from "../../Popup";
import AddCard from "./AddCard";
import ViewOrder from "./ViewOrder";
import FormErrorsOutput from "../../form/FormErrorsOutput";
import {RequestCreateBatchOrder} from "../../../api/batch-order/createBatchOrder";
import usePrint from "../../../hooks/usePrint";
import {getBatchReceipt} from "../../../api/batch-order/getBatchReceipt";
import {CentsToDollars, DollarsToCents} from "../../../utils/formatPrice";
import { GetProgramFees, GetProvisionalTax } from '../../../api';


const CreateBatchOrder = ({setShowCreateItem}) => {

    const { t } = useTranslation();

    const Steps = Object.freeze({
        ADD_CARD: 1,
        ADD_PAYMENT: 2,
        FINALIZE: 3,
        VIEW_ORDER: 4,
        CONFIRMATION: 5
    });

    const [formData, setFormData] = useState({
        corporate_customer_id: "",
        client_company_name: "",
        subtotal_cost: 0,
        total_cost: 0,
        total_tax: 0,
        total_service_fee: 0,
        office_id: null,
        office_name: '',
        quantity: 0,
        card: [],
        payments: [],
        program_id: null,
        sell_order_small_batch_fee: null,
        enable_tax_on_activation_fee: false,
        province_code: '',
        provisional_tax_rate: 0,
        skip_activation_fee: false
    });

    const [sendingData, setSendingData] = useState(false);
    const [activeStep, setActiveStep] = useState(Steps.ADD_CARD);
    const [progress, setProgress] = useState(10);
    const [disabledContinueButton, setDisabledContinueButton] = useState(true);
    const [visibleContinueButton, setVisibleContinueButton] = useState(true);
    const [visibleCreateButton, setVisibleCreateButton] = useState(false);
    const [visibleDismissButton, setVisibleDismissButton] = useState(false);
    const [disabledBackButton, setDisabledBackButton] = useState(true);
    const [visibleBackButton, setVisibleBackButton] = useState(true);
    const [disabledCreateButton, setDisabledCreateButton] = useState(true);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState([]);
    const [createdOrder, setCreatedOrder] = useState({});

    const handleBackButtonClick = () => {
        setActiveStep(activeStep - 1);
    }

    const handleCreateButton = async (e) => {
        e.preventDefault();
        setSendingData(true);
        setDisabledBackButton(true);
        try {
            const {data} = await RequestCreateBatchOrder({
                corporate_customer_id: formData.corporate_customer_id,
                total_cost: DollarsToCents(formData.total_cost),// Convert to cents
                total_tax: DollarsToCents(formData.total_tax), // Convert to cents
                office_id: formData.office_id,
                quantity: formData.quantity,
                start_processor_reference: formData.card[0].start_processor_reference,
                end_processor_reference: formData.card[0].end_processor_reference,
                load_amount: DollarsToCents(formData.card[0].load_amount), // Convert to cents
                payments: formData.payments.map(payment => ({
                    amount: DollarsToCents(payment.amount), // Convert to cents
                    payment_type: payment.payment_type.value,
                    confirmation_code: payment.confirmation_code,
                })),
                skip_activation_fee: formData.skip_activation_fee
            });
            setActiveStep(Steps.CONFIRMATION);
            setCreatedOrder(data);
        } catch (error){
            setError(error.message);
            if (error.data) {
                setFieldErrors(error.data);
            }
            console.log('Failed to create sell order with error:', error);
        }
        setSendingData(false);
        setDisabledBackButton(false);
    }

    const { handlePrint } = usePrint(getBatchReceipt);

    // Default actions on step change
    useEffect(() => {

        setError('');
        setFieldErrors([]);

        // Back button enabled by default on activeStep larger than 1
        if (activeStep > 1) {
            setDisabledBackButton(false);
        } else {
            setDisabledBackButton(true);
        }

        // Add Card
        if(activeStep === Steps.ADD_CARD){
            setProgress(10);
            setVisibleCreateButton(false);
            setVisibleContinueButton(true);
        }

        // Add Payment
        if(activeStep === Steps.ADD_PAYMENT){
            setProgress(50);
            setDisabledCreateButton(true);
            setVisibleCreateButton(true);
            setVisibleContinueButton(false);
        }

        //Finalize - skipped
        // if(activeStep === Steps.FINALIZE){
        //     setProgress(65);
        //     setVisibleContinueButton(false);
        //     setVisibleCreateButton(true);
        // } else {
        //     setVisibleContinueButton(true);
        //     setVisibleCreateButton(false);
        // }

        //View Order
        if (activeStep === Steps.VIEW_ORDER){
            setProgress(95);
            setVisibleContinueButton(false);
            setVisibleCreateButton(true);
        }

        // Confirmation Message
        if (activeStep === Steps.CONFIRMATION){
            setProgress(100);
            setVisibleCreateButton(false);
            setVisibleContinueButton(false);
            setVisibleBackButton(false);
            setVisibleDismissButton(true);
        }

    }, [activeStep]);


    useEffect(() => {
        if(activeStep === Steps.ADD_CARD){

            let prnValid = true;

            if(!formData.card.length){
                prnValid = false;
            }
            formData.card.forEach((el) => {
                if (!el.start_processor_reference || !el.end_processor_reference || !el.card_type || !el.load_amount) {
                    prnValid = false;
                }
            });

            if(prnValid && formData.office_id && formData.card[0].load_amount){
                setDisabledContinueButton(false);
            } else {
                setDisabledContinueButton(true);
            }
        }

        if(activeStep === Steps.ADD_PAYMENT){

            let allPaymentsValid = true;

            if(!formData.payments.length){
                allPaymentsValid = false;
            }
            formData.payments.forEach((el) => {
                if (!el.payment_type || !el.confirmation_code || !(el.amount > 0)) {
                    allPaymentsValid = false;
                }
            });

            if(allPaymentsValid && formData.office_id && (formData.total_cost - formData.total_collected) === 0){
                setDisabledCreateButton(false);
            } else {
                setDisabledCreateButton(true);
            }
        }
    }, [activeStep, formData]);

    useEffect(() => {
      async function getProgramFees() {
        // get program fees
        try {
          const programFeeRes = await GetProgramFees({
            program_id: formData.program_id,
          });
          setFormData(prev => ({
            ...prev,
            sell_order_small_batch_fee: Number(CentsToDollars(programFeeRes?.data?.sell_order_small_batch_fee ?? 0)),
            enable_tax_on_activation_fee: programFeeRes?.data?.enable_tax_on_activation_fee ?? false,
          }));
        } catch (error) {
          console.log(error);
        }
      }
      if (formData.program_id) {
        getProgramFees();
      }
    }, [formData?.program_id]);

    useEffect(() => {
        async function getProvisioanlTax() {
          // get program fees
          try {
            const programFeeRes = await GetProvisionalTax({
              province_code: formData.province_code,
            });
            setFormData(prev => ({
              ...prev,
              provisional_tax_rate: (programFeeRes?.data?.tax_rate || 0) / 100,
            }));
          } catch (error) {
            console.log(error);
          }
        }
        if (formData.province_code) {
          getProvisioanlTax();
        }
      }, [formData?.province_code]);

    const MAXCARDLIMIT = 10;

    useEffect(() => {
        // Update formData when cards or office_id changes
            const FEE_PER_CARD = Number(formData?.sell_order_small_batch_fee ?? 0);
            const PROVISIONAL_TAX_RATE = Number(formData?.provisional_tax_rate ?? 0);
            const cardsCount = formData.quantity;
            const serviceFeeApplicable = cardsCount < MAXCARDLIMIT;
            const loadAmount = Number(formData.card[0]?.load_amount || 0);

            // Calculate subtotal
            const subtotal = Number(loadAmount * cardsCount || 0);

            // Calculate fee and tax only if applicable
            const baseServiceFee = FEE_PER_CARD * cardsCount;
            // Taxed only when the program enables it, rounded in cents like bps-go.
            const totalTax = serviceFeeApplicable && formData.enable_tax_on_activation_fee
                ? Math.round(DollarsToCents(baseServiceFee) * PROVISIONAL_TAX_RATE) / 100
                : 0;
            const totalServiceFee = serviceFeeApplicable ? baseServiceFee : 0;
            const totalCost = subtotal + totalServiceFee + totalTax;

            // Convert to float with 2 decimal digits
            const toFloatFixed2 = (num) => {
                const floatNum = Number(num); // or parseFloat(num)
                return Number(floatNum.toFixed(2));
            };
            if(formData.skip_activation_fee){
                setFormData(prev => ({
                    ...prev,
                    subtotal_cost: toFloatFixed2(subtotal),
                    total_cost: toFloatFixed2(subtotal),
                    total_tax: 0,
                    total_service_fee: 0,
                }));
                return
            }

            setFormData(prev => ({
                ...prev,
                subtotal_cost: toFloatFixed2(subtotal),
                total_cost: toFloatFixed2(totalCost),
                total_tax: toFloatFixed2(totalTax),
                total_service_fee: toFloatFixed2(totalServiceFee),
            }));

    }, [formData.card, formData.quantity, formData.skip_activation_fee, formData.sell_order_small_batch_fee, formData.provisional_tax_rate, formData.enable_tax_on_activation_fee]);

    return (
        <Popup size="extra-large" withClose withOverlay onClose={() => setShowCreateItem(false)}>
            <div className="bulk-create-cards">
                <div className="multistep-form">
                    <div className="multistep-form__progress-bar">
                        <ProgressBar
                            title={t("Create Batch Order")}
                            steps={[
                                {
                                    active: (activeStep >= Steps.ADD_CARD),
                                    name: t("Add Card")
                                },
                                {
                                    active: (activeStep >= Steps.ADD_PAYMENT),
                                    name: t("Add Payment")
                                },
                                // {
                                //     active: (activeStep >= Steps.FINALIZE),
                                //     name: t("Finalize / Submit")
                                // },
                                {
                                    active: (activeStep >= Steps.VIEW_ORDER),
                                    name: t("View Order")
                                }
                            ]}
                            progress={progress}
                            class="mb-4"
                        />
                    </div>
                    <div className="multistep-form__content mb-2">
                        {activeStep === Steps.ADD_CARD &&
                            <AddCard formData={formData} setFormData={setFormData}/>}
                        {activeStep === Steps.ADD_PAYMENT &&
                            <AddPayment formData={formData} setFormData={setFormData}/>}
                        {activeStep === Steps.FINALIZE &&
                            <Finalize formData={formData} setFormData={setFormData}/>}
                        {activeStep === Steps.VIEW_ORDER &&
                            <ViewOrder/>}
                        {activeStep === Steps.CONFIRMATION &&
                            <OrderCreationMessage formData={formData} setFormData={setFormData}/>}
                    </div>
                    <FormErrorsOutput error={error} fieldErrors={fieldErrors}/>
                    <div className="multistep-form__navigation">
                        {visibleBackButton && <button
                            className="btn btn-secondary btn-medium"
                            onClick={handleBackButtonClick}
                            disabled={disabledBackButton}
                        >
                            <ArrowLeft/>{t("Back")}
                        </button>}
                        {visibleContinueButton && <button
                            className="btn btn-primary btn-medium"
                            onClick={e => setActiveStep(activeStep + 1)}
                            disabled={disabledContinueButton}
                        >
                            {t("Continue")}
                        </button>}

                        {visibleCreateButton && <button
                            className={`btn btn-primary btn-medium ${sendingData ? 'btn-loading' : ''}`}
                            onClick={handleCreateButton}
                            disabled={disabledCreateButton || sendingData}
                        >
                            {sendingData ? <LoadingIcon/> : ""}
                            Place Order
                        </button>}

                        {visibleDismissButton && <div className="text-right" style={{width: '100%'}}>
                            <button
                                className={`btn btn-primary btn-medium`}
                                onClick={e => setShowCreateItem(false)}
                            >{t("Done")}
                            </button>
                        </div>}
                    </div>
                </div>

            </div>
        </Popup>
    );
};

export default CreateBatchOrder;
